/**
 * Shared Firewall spam test helper — single source for scenario payloads and result formatting.
 */

const FUNCTION_URL =
  process.env.REACT_APP_FUNCTION_URL ||
  'https://handlesubmission-5sg7uqqiaq-uc.a.run.app';

export const SPAM_TEST_SCENARIOS = [
  {
    id: 'clean',
    label: 'Clean inquiry',
    description: 'Generic information-seeking inquiry — should pass on any business.',
    tone: 'clean',
  },
  {
    id: 'contact-only-real',
    label: 'Contact-only (real person)',
    description: 'Plausible name + email, no message — should pass clean',
    tone: 'clean',
  },
  {
    id: 'gibberish-identity',
    label: 'Gibberish identity',
    description: 'Random name/email, no message — likely spam, AI held',
    tone: 'warn',
  },
  {
    id: 'honeypot',
    label: 'Honeypot filled',
    description: 'Hidden field filled by a bot',
    tone: 'danger',
  },
  {
    id: 'keywords',
    label: 'Spam keywords',
    description: 'Obvious spam words like viagra, casino',
    tone: 'danger',
  },
  {
    id: 'links',
    label: 'Link spam',
    description: 'Multiple URLs in the message body',
    tone: 'danger',
  },
  {
    id: 'temp-email',
    label: 'Temp email',
    description: 'Disposable email domain',
    tone: 'warn',
  },
  {
    id: 'all-caps',
    label: 'All caps',
    description: 'Shouting / excessive capitalization',
    tone: 'warn',
  },
];

function buildScenarioData(testType) {
  switch (testType) {
    case 'clean':
      return {
        name: 'Sarah Mitchell',
        email: `sarah.mitchell${Math.floor(Math.random() * 1000)}@gmail.com`,
        message:
          "Hi, I came across your website and found it really helpful. I have a question that I couldn't find the answer to on the site — what's the best way to get more information or speak with someone? Thanks!",
      };
    case 'contact-only-real':
      return {
        name: 'Carrie Junge',
        email: `carrie.junge${Math.floor(Math.random() * 1000)}@vasservice.com`,
        message: '',
      };
    case 'gibberish-identity':
      return {
        name: 'fjbjf fjbf',
        email: 'nfjknf@gmail.com',
        message: '',
      };
    case 'honeypot':
      return {
        name: 'James Wilson',
        email: `jameswilson${Math.floor(Math.random() * 10000)}@gmail.com`,
        message: 'Test honeypot detection',
        _hp_field: 'filled',
      };
    case 'keywords':
      return {
        name: 'Spammer',
        email: 'viagra@casino.com',
        message: 'Click here for free money and casino loans! Act now!',
      };
    case 'links':
      return {
        name: 'Link Spammer',
        email: 'links@example.com',
        message:
          'Check out https://example.com and https://test.com and https://spam.com and https://more.com',
      };
    case 'temp-email':
      return {
        name: 'Temp User',
        email: 'test@tempmail.com',
        message: 'Using temporary email',
      };
    case 'all-caps':
      return {
        name: 'SHOUTER',
        email: 'shout@example.com',
        message: 'THIS IS A MESSAGE WITH ALL CAPS AND EXCESSIVE CAPITALIZATION',
      };
    default:
      return null;
  }
}

export function verdictFromResponse(resultData) {
  if (resultData?.liveBehavior?.verdict === 'quarantined' || resultData?.spamStatus === 'spam') {
    return 'spam';
  }
  if (resultData?.liveBehavior?.verdict === 'likely_spam' || resultData?.spamStatus === 'suspicious') {
    return 'suspicious';
  }
  return 'clean';
}

export function verdictLabel(verdict) {
  if (verdict === 'spam') return 'QUARANTINED';
  if (verdict === 'suspicious') return 'LIKELY SPAM';
  return 'CLEAN';
}

export function previewLiveBehavior(formSettings, workspaceSettings, verdict) {
  const autoReply = formSettings?.autoReply !== false && formSettings?.knowledgeBaseId;
  const autoSuspicious = formSettings?.autoReplyToSuspicious === true;

  if (verdict === 'spam') {
    return 'Silently quarantined — you never see it, the bot thinks it worked';
  }
  if (verdict === 'suspicious') {
    if (autoReply && autoSuspicious) {
      return 'AI replies automatically (auto-reply to suspicious is ON)';
    }
    return 'Lead is flagged for your review — AI holds';
  }
  if (autoReply) {
    return 'AI replies automatically';
  }
  return 'Lead captured — AI reply not configured';
}

export async function submitSpamTest({ formId, testType, customData }) {
  const testData =
    testType === 'custom' ? customData : buildScenarioData(testType);
  if (!testData) {
    throw new Error(`Unknown test type: ${testType}`);
  }

  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      formId,
      data: testData,
      trackingData: {
        test: true,
        testType,
        timestamp: new Date().toISOString(),
      },
    }),
  });

  const resultData = await response.json();
  const verdict = verdictFromResponse(resultData);

  return {
    success: response.ok,
    data: resultData,
    testType,
    submittedData: testData,
    verdict,
    verdictLabel: verdictLabel(verdict),
    liveBehaviorSummary:
      resultData.liveBehaviorSummary ||
      previewLiveBehavior({}, {}, verdict),
  };
}

export function formatSpamTestResult(result, formSettings, workspaceSettings) {
  const verdict = result.verdict || verdictFromResponse(result.data);
  const liveLine =
    result.data?.liveBehaviorSummary ||
    previewLiveBehavior(formSettings, workspaceSettings, verdict);

  const messages = {
    clean: 'Clean submission',
    suspicious: 'Likely spam — flagged for review',
    spam: 'Quarantined — silently discarded',
  };

  return {
    ...result,
    resultStatus: verdict,
    resultMessage: messages[verdict] || messages.clean,
    liveBehaviorSummary: liveLine,
    testModeNote:
      'Test mode: no email notifications or AI replies are sent. IP checks are skipped in test mode.',
  };
}

export { buildScenarioData, FUNCTION_URL };
